// lib/vector-artboards.ts
// The artboard sizes of Studio Vector. Like Illustrator's New Document, a size is chosen before
// anything is drawn; the artboard is then the fixed window the editor shows.

export type ArtboardSize = { width: number; height: number };

export const ARTBOARD_MIN_SIDE = 16;
export const ARTBOARD_MAX_SIDE = 8192;

export const ARTBOARD_PRESETS: ReadonlyArray<{ id: string; label: string; detail: string; size: ArtboardSize }> = [
  { id: 'logo', label: 'Logo', detail: '1000 × 1000', size: { width: 1000, height: 1000 } },
  { id: 'icon', label: 'Icon', detail: '512 × 512', size: { width: 512, height: 512 } },
  { id: 'wordmark', label: 'Wordmark', detail: '2000 × 600', size: { width: 2000, height: 600 } },
  { id: 'landscape', label: 'Landscape', detail: '1920 × 1080', size: { width: 1920, height: 1080 } },
  { id: 'portrait', label: 'Story', detail: '1080 × 1920', size: { width: 1080, height: 1920 } },
  { id: 'a4', label: 'A4', detail: '2480 × 3508', size: { width: 2480, height: 3508 } },
];

export const DEFAULT_ARTBOARD_SIZE: ArtboardSize = ARTBOARD_PRESETS[0]!.size;

/** A whole-pixel size inside the bounds, or null for anything that is not a usable size. */
export const normalizeArtboardSize = (value: unknown): ArtboardSize | null => {
  const record = value as { width?: unknown; height?: unknown } | null;
  const side = (v: unknown) => {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : Number.NaN;
    return Number.isFinite(n) ? Math.round(n) : Number.NaN;
  };
  const width = side(record?.width);
  const height = side(record?.height);
  const ok = (n: number) => n >= ARTBOARD_MIN_SIDE && n <= ARTBOARD_MAX_SIDE;
  return ok(width) && ok(height) ? { width, height } : null;
};

/**
 * Smart guides: the smallest move bringing one of `values` onto one of `lines`, within `tolerance`.
 * Returns the move and the line it lands on, or null when nothing is that close.
 */
export const nearestSnap = (values: readonly number[], lines: readonly number[], tolerance: number): { delta: number; line: number } | null => {
  let best: { delta: number; line: number } | null = null;
  for (const value of values) {
    for (const line of lines) {
      const delta = line - value;
      if (Math.abs(delta) <= tolerance && (!best || Math.abs(delta) < Math.abs(best.delta))) best = { delta, line };
    }
  }
  return best;
};
